/**
 * Login streak service — tracks consecutive login days and awards badges
 */
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { awardBadge } from './badgeService';
import { BADGES } from '../data/badges';
import { createLogger } from '../utils/logger';

const log = createLogger('loginStreakService');

/**
 * Record a login and check/award streak badges.
 * Called once per authentication session from AuthContext.
 * @param {string} userId
 */
export const recordLogin = async (userId) => {
  try {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10); // YYYY-MM-DD
    const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    const docRef = doc(db, 'loginStreaks', userId);
    const snap = await getDoc(docRef);
    const data = snap.exists() ? snap.data() : null;

    // Already recorded today — skip
    if (data?.lastLoginDate === todayStr) return;

    let streak = 1;

    if (data?.lastLoginDate) {
      const lastDate = new Date(data.lastLoginDate + 'T00:00:00');
      const todayDate = new Date(todayStr + 'T00:00:00');
      const diffDays = Math.round((todayDate - lastDate) / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        // Consecutive day
        streak = (data.currentStreak || 1) + 1;
      }
      // diffDays > 1 means streak resets to 1
    }

    await setDoc(docRef, {
      lastLoginDate: todayStr,
      currentStreak: streak,
      longestStreak: Math.max(streak, data?.longestStreak || 0),
    });

    log.info('Login recorded', { userId, streak, isWeekend });

    // Award badges (fire-and-forget)
    const badgeChecks = [];

    if (streak >= 2) {
      badgeChecks.push(awardBadge({ userId, ...BADGES.COMEBACK_KID }));
    }
    if (streak >= 10) {
      badgeChecks.push(awardBadge({ userId, ...BADGES.CREATURE_OF_HABIT }));
    }
    if (streak >= 20) {
      badgeChecks.push(awardBadge({ userId, ...BADGES.UNSTOPPABLE }));
    }
    if (isWeekend) {
      badgeChecks.push(awardBadge({ userId, ...BADGES.WEEKEND_WARRIOR }));
    }

    await Promise.all(badgeChecks);
  } catch (err) {
    log.error('Failed to record login', { userId, error: err.message });
  }
};
